/**
 * 방패 그림 시트(`public/sprites/shields.png`, `tools/shields_pack.py`가 만든다).
 *
 * 방패는 인물 그림과 따로 그린다 — 부서지면 방패만 사라져야 해서다(플레이한 사람: "방패를 스프라이트에 합쳐그리면
 * 안되고 따로 그려야 한다"). 칸은 `SHIELD_CELL`픽셀, 줄 = 나라(고구려 · 백제 · 신라), 칸 = 작은 방패 앞·옆·뒤 ·
 * 큰 방패 앞·옆·뒤. 그림은 병사 그림과 같은 논리 픽셀이라 화면에 올릴 때도 병사와 같은 배율(`SHEET_SCALE`)을 쓴다.
 */
import { ImageSource, Rectangle, Texture } from 'pixi.js';
import { Culture } from '../sim/troops.ts';

export const SHIELD_CELL = 24;

/** 방패의 어느 면이 보이는가. 칸 순서와 같다. */
export const ShieldView = { Front: 0, Side: 1, Back: 2 } as const;
export type ShieldView = (typeof ShieldView)[keyof typeof ShieldView];

export interface ShieldSheet {
  /** 나라·크기·면의 그림. */
  texture(culture: Culture, big: boolean, view: ShieldView): Texture;
}

const ROW: Record<Culture, number> = { [Culture.Goguryeo]: 0, [Culture.Baekje]: 1, [Culture.Silla]: 2 };

export function shieldSheetUrl(base: string = import.meta.env.BASE_URL): string {
  return `${base}sprites/shields.png`;
}

let image: Promise<HTMLImageElement | null> | null = null;

/** 시트에서 이 방패(앞면)가 앉은 칸의 왼쪽 위. 화면의 작은 그림(`ui/unit-icon.ts`)이 전장과 같은 칸을 잘라 쓴다. */
export function shieldCellAt(culture: Culture, big: boolean, view: ShieldView): { x: number; y: number } {
  return { x: ((big ? 3 : 0) + view) * SHIELD_CELL, y: (ROW[culture] ?? 0) * SHIELD_CELL };
}

/** 시트 그림 그대로(GPU 텍스처 없이). 화면의 작은 그림이 캔버스에 얹을 때 쓴다. 받지 못하면 null. */
export function loadShieldImage(base: string = import.meta.env.BASE_URL): Promise<HTMLImageElement | null> {
  return loadImage(base);
}

/** 그림 한 장. 받지 못하면 null — 렌더러는 코드로 그린 옛 방패(`atlas.ts`의 `shield0/1`)를 쓴다. */
function loadImage(base: string): Promise<HTMLImageElement | null> {
  const attempt = (image ??= new Promise<HTMLImageElement | null>((res) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = shieldSheetUrl(base);
  }));
  // 실패는 붙들지 않는다 — 다음 전투에서 다시 받는다.
  void attempt.then((got) => {
    if (!got && image === attempt) image = null;
  });
  return attempt;
}

/**
 * 시트를 불러온다. GPU 텍스처는 전투마다 새로 만든다(`loadUnitSheet`와 같은 까닭 — 전투가 끝나면 렌더러가 텍스처를
 * 버린다).
 */
export async function loadShieldSheet(base: string = import.meta.env.BASE_URL): Promise<ShieldSheet | null> {
  const img = await loadImage(base);
  if (!img) return null;
  const src = new Texture({ source: new ImageSource({ resource: img, scaleMode: 'nearest' }) });
  const cache = new Map<number, Texture>();
  return {
    texture(culture: Culture, big: boolean, view: ShieldView): Texture {
      const at = shieldCellAt(culture, big, view);
      const key = at.y * 8 + at.x;
      let t = cache.get(key);
      if (!t) {
        t = new Texture({ source: src.source, frame: new Rectangle(at.x, at.y, SHIELD_CELL, SHIELD_CELL) });
        cache.set(key, t);
      }
      return t;
    },
  };
}
